import {
    areSearchParamsEqual,
    PERIOD_STATUSES_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type PeriodStatusesFilterItem = { key: string; value: string };

export type PeriodStatusesUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    userName: string | null;
    branchId: number | null;
    childWindow: boolean;
    filterStatusesId: PeriodStatusesFilterItem | null;
    filterStatusIds: PeriodStatusesFilterItem[] | null;
    filterChangeType: PeriodStatusesFilterItem | null;
    filterDateTime: Date | null;
    filterEndDateTime: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    hasStartFilter: boolean;
    filterFinishDateTime: Date | null;
    filterFinishEndDateTime: Date | null;
    startFinishTime: Date | null;
    endFinishTime: Date | null;
    hasEndFilter: boolean;
    timeInStatusStartTime: Date | null;
    timeInStatusEndTime: Date | null;
    uiFilters: Record<string, any>;
};

export type PeriodStatusesUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    userName: string | null;
    branchId: number | null;
    childWindow: boolean;
    filterStatusesId: PeriodStatusesFilterItem | null;
    filterStatusIds: PeriodStatusesFilterItem[] | null;
    filterChangeType: PeriodStatusesFilterItem | null;
    filterDateTime: any;
    filterEndDateTime: any;
    startTime: any;
    endTime: any;
    filterFinishDateTime: any;
    filterFinishEndDateTime: any;
    startFinishTime: any;
    endFinishTime: any;
    hasEndFilter: boolean;
    timeInStatusStartTime: any;
    timeInStatusEndTime: any;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialPeriodStatusesUrlState(): PeriodStatusesUrlViewState {
    return parsePeriodStatusesUrlState(getHashSearchParams());
}

export function parsePeriodStatusesUrlState(params: URLSearchParams): PeriodStatusesUrlViewState {
    const childWindow = params.get('childWindow') === '1';
    const legacyFrom = params.get('from');
    const legacyTo = params.get('to');
    const legacyUserName = params.get('userName');
    const legacyBranchId = params.get('branchId');

    params = unwrapEncodedSearchParams(params, PERIOD_STATUSES_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const userName = params.get('userName') || legacyUserName;
    const branchIdRaw = params.get('branchId') || legacyBranchId;
    const branchId = branchIdRaw && /^-?\d+$/.test(branchIdRaw) ? Number(branchIdRaw) : null;

    const id = params.get('id');
    const statusId = getMultiParams(params, 'statusId').map(toNumberValue);
    const changeType = params.get('changeType');
    const startRange = parseDateTimeRange(params.get('startFrom'), params.get('startTo'))
        || parseDateTimeRange(legacyFrom, legacyTo);
    const endRange = parseDateTimeRange(params.get('endFrom'), params.get('endTo'));
    const timeInStatusRange = parseSecondsRange(params.get('timeInStatusFrom'), params.get('timeInStatusTo'));

    if (id) uiFilters.id = id;
    if (statusId.length) uiFilters.statusName = statusId;
    if (changeType) uiFilters.changeType = changeType;
    if (endRange) uiFilters.timeEnd = endRange.startDate;
    if (timeInStatusRange) uiFilters.timeInStatus = timeInStatusRange.start;

    const hasLegacy = !!(legacyFrom || legacyTo || legacyUserName || legacyBranchId);

    return {
        hasAny: params.toString().length > 0 || hasLegacy,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        userName: userName || null,
        branchId,
        childWindow,
        filterStatusesId: toFilterItem('Id', id),
        filterStatusIds: toFilterList('StatusId', statusId),
        filterChangeType: toFilterItem('ChangeType', changeType),
        filterDateTime: startRange?.startDate ?? null,
        filterEndDateTime: startRange?.endDate ?? null,
        startTime: startRange?.startTime ?? null,
        endTime: startRange?.endTime ?? null,
        hasStartFilter: !!startRange || !!userName,
        filterFinishDateTime: endRange?.startDate ?? null,
        filterFinishEndDateTime: endRange?.endDate ?? null,
        startFinishTime: endRange?.startTime ?? null,
        endFinishTime: endRange?.endTime ?? null,
        hasEndFilter: !!endRange,
        timeInStatusStartTime: timeInStatusRange?.start ?? null,
        timeInStatusEndTime: timeInStatusRange?.end ?? null,
        uiFilters,
    };
}

export function buildPeriodStatusesSearchParams(state: PeriodStatusesUrlWriteState): URLSearchParams {
    const encoded = wrapEncodedSearchParams(
        buildPlainPeriodStatusesSearchParams(state),
        PERIOD_STATUSES_ENCODED_QUERY_KEY
    );
    if (state.childWindow) {
        encoded.set('childWindow', '1');
    }
    return encoded;
}

function buildPlainPeriodStatusesSearchParams(state: PeriodStatusesUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'TimeStart' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }

    if (state.userName) {
        params.set('userName', state.userName);
    }
    if (state.branchId != null) {
        params.set('branchId', String(state.branchId));
    }

    setScalar(params, 'id', state.filterStatusesId?.value);
    setMulti(params, 'statusId', state.filterStatusIds);
    setScalar(params, 'changeType', state.filterChangeType?.value);

    if (shouldWriteStartRange(state)) {
        const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
        const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
        if (startFrom) params.set('startFrom', toLocalIso(startFrom));
        if (startTo) params.set('startTo', toLocalIso(startTo));
    }

    if (state.hasEndFilter || hasFinishRange(state)) {
        const endFrom = combineDateAndTime(state.filterFinishDateTime, state.startFinishTime, true);
        const endTo = combineDateAndTime(state.filterFinishEndDateTime, state.endFinishTime, false);
        if (endFrom) params.set('endFrom', toLocalIso(endFrom));
        if (endTo) params.set('endTo', toLocalIso(endTo));
    }

    const timeFrom = getTotalSeconds(state.timeInStatusStartTime);
    const timeTo = getTotalSeconds(state.timeInStatusEndTime);
    if (timeFrom != null && timeTo != null && !(timeFrom === 0 && timeTo === 0)) {
        params.set('timeInStatusFrom', String(timeFrom));
        params.set('timeInStatusTo', String(timeTo));
    }

    return params;
}

function shouldWriteStartRange(state: PeriodStatusesUrlWriteState): boolean {
    const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
    const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
    if (!startFrom || !startTo) return false;
    if (state.userName) return true;
    return !isDefaultDateRange(startFrom, startTo);
}

function hasFinishRange(state: PeriodStatusesUrlWriteState): boolean {
    return !!(state.filterFinishDateTime && state.filterFinishEndDateTime && state.startFinishTime && state.endFinishTime);
}

function isDefaultDateRange(startFrom: Date, startTo: Date): boolean {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 0);
    return toLocalIso(startFrom) === toLocalIso(todayStart) && toLocalIso(startTo) === toLocalIso(todayEnd);
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function setMulti(params: URLSearchParams, key: string, items?: PeriodStatusesFilterItem[] | null) {
    items?.forEach((item) => {
        if (item?.value) {
            params.append(key, item.value);
        }
    });
}

function getMultiParams(params: URLSearchParams, key: string): string[] {
    const values: string[] = [];
    params.getAll(key).forEach((value) => {
        value.split(',').forEach((part) => {
            const trimmed = part.trim();
            if (trimmed) values.push(trimmed);
        });
    });
    return values;
}

function toFilterItem(key: string, value?: string | null): PeriodStatusesFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function toFilterList(key: string, values?: Array<string | number>): PeriodStatusesFilterItem[] | null {
    if (!values?.length) return null;
    return values.map((value) => ({ key, value: String(value) }));
}

function toNumberValue(value: string): number | string {
    if (/^-?\d+$/.test(value)) {
        const parsed = Number(value);
        if (Number.isSafeInteger(parsed)) {
            return parsed;
        }
    }
    return value;
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}

function toLocalIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function fromLocalIso(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
    );
}

function parseDateTimeRange(fromValue: string | null, toValue: string | null) {
    if (!fromValue || !toValue) return null;
    const from = fromLocalIso(fromValue);
    const to = fromLocalIso(toValue);
    if (!from || !to) return null;

    return {
        startDate: startOfDay(from),
        endDate: startOfDay(to),
        startTime: timeOnly(from),
        endTime: timeOnly(to),
    };
}

function parseSecondsRange(fromValue: string | null, toValue: string | null) {
    if (fromValue == null || toValue == null) return null;
    const from = Number(fromValue);
    const to = Number(toValue);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
    if (from === 0 && to === 0) return null;
    return {
        start: secondsToDate(from),
        end: secondsToDate(to),
    };
}

function startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function timeOnly(date: Date): Date {
    const next = new Date();
    next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return next;
}

function secondsToDate(totalSeconds: number): Date {
    const next = new Date();
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    next.setHours(hours, minutes, seconds, 0);
    return next;
}

function getTotalSeconds(date: any): number | null {
    if (!date || typeof date.getHours !== 'function') return null;
    return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

function combineDateAndTime(dateValue: any, timeValue: any, isStart: boolean): Date | null {
    const date = toValidDate(dateValue);
    if (!date) return null;
    const time = toValidDate(timeValue);
    const result = new Date(date);
    if (time) {
        result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    } else {
        result.setHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, 0);
    }
    return result;
}

function toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
